import { NonZeroValueExchangeStrategy } from "@redotech/redo-common-model/exchange";
import { DEFAULT_DUPLICATE_ORDER_NUMBER_FORMAT } from "@redotech/redo-order-core-common/order";
import { EXCHANGE_ORDER_PREFIX_TYPES } from "@redotech/redo-return-core-common/exchange";
import {
  ExchangeFinancialStrategy,
  ExchangeOrderLineItemPriceSource,
  NonInstantExchangeOrderProvision,
} from "@redotech/redo-return-core-common/exchange-settings";
import { ShopifyExchangeOrderStrategy } from "@redotech/redo-shopify-common/shopify";
import { isDefined } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import type {
  OperationsSettingsPatch,
  ReturnsOperationsSettingsSource,
} from "./operations-settings-source";

export enum ExchangeCreateTrigger {
  CREATION = "creation",
  CREATION_UPSELLS = "creation_upsells",
  RETURN_APPROVED = "return_approved",
  EXCHANGE_PRODUCTS_PROCESSED = "exchange_products_processed",
  ALL_PRODUCTS_PROCESSED = "all_products_processed",
}

export const DEFAULT_FEE_LINE_ITEM_NAME = "Return Fee";

export type DuplicateOrderNumberFormatError =
  | "missing_placeholder"
  | "too_long"
  | "numeric_prefix";

export function duplicateOrderNumberFormatError(
  value: string,
): DuplicateOrderNumberFormatError | undefined {
  if (!value.includes("{n}")) {
    return "missing_placeholder";
  }
  if (value.length > 10) {
    return "too_long";
  }
  const charBefore = value[value.indexOf("{n}") - 1];
  if (!charBefore || /\d/.test(charBefore)) {
    return "numeric_prefix";
  }
  return undefined;
}

export const ordersSettingsShape = {
  orders: z.object({
    reuseOrderName: z.boolean(),
    shouldStripHash: z.boolean(),
    exchangeShippingName: z.string(),
    prefix: z.string(),
    suffix: z.string(),
    duplicateOrderNumberFormat: z
      .string()
      .refine((value) => duplicateOrderNumberFormatError(value) === undefined, {
        message: "Invalid duplicate order number format",
      }),
    orderNamesByReturnType: z.array(
      z.object({
        returnType: z.enum(EXCHANGE_ORDER_PREFIX_TYPES),
        prefix: z.string(),
        suffix: z.string(),
      }),
    ),
    exchangeFinancialStrategy: createEnum(ExchangeFinancialStrategy),
    exchangeOrderLineItemPriceSource: createEnum(
      ExchangeOrderLineItemPriceSource,
    ),
    addTaxToExchangeOrders: z.boolean(),
    applyPaymentGateways: z.boolean(),
    upsellGateway: z.string(),
    returnValueGateway: z.string(),
    /** Shown instead of the pricing method when it doesn't apply. */
    nonZeroValueExchangeStrategy: createEnum(NonZeroValueExchangeStrategy),
    createTrigger: createEnum(ExchangeCreateTrigger),
    /** Only meaningful while orders are created on return creation. */
    createLabelOnDraftOrderComplete: z.boolean(),
    /** Null when the team has no Shopify integration. */
    shopifyExchangeOrderStrategy: createEnum(
      ShopifyExchangeOrderStrategy,
    ).nullable(),
    addAdditionalExchangeTags: z.boolean(),
    shouldRemoveHoldTagWhenRelease: z.boolean(),
    separateQuantityForExchangeProducts: z.boolean(),
    applyFeesAsLineItem: z.boolean(),
    applyFeesAsLineItemName: z.string(),
  }),
  orderNotes: z.object({
    disabled: z.boolean(),
    includeDate: z.boolean(),
    includeExistingNote: z.boolean(),
    addNoteToOriginalOrders: z.boolean(),
  }),
  fulfillmentRegions: z.object({
    enabled: z.boolean(),
    regionMappings: z.array(
      z.object({ country: z.string(), locationIds: z.array(z.string()) }),
    ),
    defaultLocationIds: z.array(z.string()),
  }),
};

export type OrdersSettings = z.infer<z.ZodObject<typeof ordersSettingsShape>>;

export function exchangeCreateTriggerFromSettings(exchanges: {
  enableOnHoldOrders: boolean;
  onHoldOrdersOnUpsellOnly?: boolean | null;
  nonInstantExchangeOrderProvision: NonInstantExchangeOrderProvision;
}): ExchangeCreateTrigger {
  if (exchanges.enableOnHoldOrders) {
    return exchanges.onHoldOrdersOnUpsellOnly
      ? ExchangeCreateTrigger.CREATION_UPSELLS
      : ExchangeCreateTrigger.CREATION;
  }
  switch (exchanges.nonInstantExchangeOrderProvision) {
    case NonInstantExchangeOrderProvision.ALL_PRODUCTS_PROCESSED:
      return ExchangeCreateTrigger.ALL_PRODUCTS_PROCESSED;
    case NonInstantExchangeOrderProvision.RETURN_APPROVED:
      return ExchangeCreateTrigger.RETURN_APPROVED;
    case NonInstantExchangeOrderProvision.EXCHANGE_PRODUCTS_PROCESSED:
      return ExchangeCreateTrigger.EXCHANGE_PRODUCTS_PROCESSED;
  }
}

function nonInstantProvisionFromTrigger(
  trigger: ExchangeCreateTrigger,
): NonInstantExchangeOrderProvision {
  switch (trigger) {
    case ExchangeCreateTrigger.EXCHANGE_PRODUCTS_PROCESSED:
      return NonInstantExchangeOrderProvision.EXCHANGE_PRODUCTS_PROCESSED;
    case ExchangeCreateTrigger.RETURN_APPROVED:
      return NonInstantExchangeOrderProvision.RETURN_APPROVED;
    case ExchangeCreateTrigger.CREATION:
    case ExchangeCreateTrigger.CREATION_UPSELLS:
    case ExchangeCreateTrigger.ALL_PRODUCTS_PROCESSED:
      return NonInstantExchangeOrderProvision.ALL_PRODUCTS_PROCESSED;
  }
}

export function ordersSettingsFromTeam({
  settings,
  shopifyExchangeOrderStrategy,
}: ReturnsOperationsSettingsSource): OrdersSettings {
  const { exchanges } = settings;
  return {
    orders: {
      reuseOrderName: !exchanges.orderName.useShopifyGeneratedName,
      shouldStripHash: exchanges.shouldStripHash,
      exchangeShippingName: exchanges.exchangeShippingName ?? "",
      prefix: exchanges.orderName.prefix ?? "",
      suffix: isDefined(exchanges.suffix)
        ? `-${exchanges.suffix}`
        : (exchanges.orderName.suffix ?? ""),
      duplicateOrderNumberFormat:
        exchanges.orderName.duplicateOrderNumberFormat ??
        DEFAULT_DUPLICATE_ORDER_NUMBER_FORMAT,
      orderNamesByReturnType: (
        exchanges.orderName.orderNamesByReturnType ?? []
      ).map(({ returnType, prefix, suffix }) => ({
        returnType,
        prefix,
        suffix,
      })),
      exchangeFinancialStrategy:
        exchanges.exchangeFinancialStrategy ?? ExchangeFinancialStrategy.NONE,
      exchangeOrderLineItemPriceSource:
        exchanges.exchangeOrderLineItemPriceSource ??
        ExchangeOrderLineItemPriceSource.DISCOUNTED_PRICE,
      addTaxToExchangeOrders: exchanges.addTaxToExchangeOrders ?? false,
      applyPaymentGateways:
        exchanges.applyExchangeOrderPaymentGateways?.enabled ?? false,
      upsellGateway:
        exchanges.applyExchangeOrderPaymentGateways?.upsellGateway ?? "",
      returnValueGateway:
        exchanges.applyExchangeOrderPaymentGateways?.returnValueGateway ?? "",
      nonZeroValueExchangeStrategy:
        exchanges.nonZeroValueExchangeStrategy ??
        NonZeroValueExchangeStrategy.NONE,
      createTrigger: exchangeCreateTriggerFromSettings(exchanges),
      createLabelOnDraftOrderComplete:
        exchanges.createLabelOnDraftOrderComplete ?? false,
      shopifyExchangeOrderStrategy: shopifyExchangeOrderStrategy ?? null,
      addAdditionalExchangeTags: exchanges.addAdditionalExchangeTags ?? false,
      shouldRemoveHoldTagWhenRelease:
        exchanges.shouldRemoveHoldTagWhenRelease ?? false,
      separateQuantityForExchangeProducts:
        exchanges.separateQuantityForExchangeProducts ?? false,
      applyFeesAsLineItem: exchanges.applyFeesAsLineItem ?? false,
      applyFeesAsLineItemName:
        exchanges.applyFeesAsLineItemName ?? DEFAULT_FEE_LINE_ITEM_NAME,
    },
    orderNotes: {
      disabled: exchanges.notes?.disabled ?? false,
      includeDate: exchanges.notes?.includeDate ?? false,
      includeExistingNote: exchanges.notes?.includeExistingNote ?? true,
      addNoteToOriginalOrders:
        exchanges.notes?.addNoteToOriginalOrders ?? false,
    },
    fulfillmentRegions: {
      enabled: exchanges.fulfillmentRegions?.enabled ?? false,
      regionMappings: exchanges.fulfillmentRegions?.regionMappings ?? [],
      defaultLocationIds:
        exchanges.fulfillmentRegions?.defaultLocationIds ?? [],
    },
  };
}

/** The Shopify exchange order strategy lives on the integration, so the aggregator writes it. */
export function ordersTeamPatch(
  { settings }: ReturnsOperationsSettingsSource,
  next: OrdersSettings,
): OperationsSettingsPatch {
  const { orders, orderNotes, fulfillmentRegions } = next;
  const createsOnReturnCreation = [
    ExchangeCreateTrigger.CREATION,
    ExchangeCreateTrigger.CREATION_UPSELLS,
  ].includes(orders.createTrigger);
  const set: Record<string, unknown> = {
    "exchanges.orderName.useShopifyGeneratedName": !orders.reuseOrderName,
    "exchanges.shouldStripHash": orders.shouldStripHash,
    "exchanges.orderName.prefix": orders.prefix,
    "exchanges.orderName.suffix": orders.suffix,
    "exchanges.orderName.duplicateOrderNumberFormat":
      orders.duplicateOrderNumberFormat,
    "exchanges.orderName.orderNamesByReturnType": orders.orderNamesByReturnType,
    "exchanges.exchangeFinancialStrategy": orders.exchangeFinancialStrategy,
    "exchanges.exchangeOrderLineItemPriceSource":
      orders.exchangeOrderLineItemPriceSource,
    "exchanges.addTaxToExchangeOrders": orders.addTaxToExchangeOrders,
    "exchanges.applyExchangeOrderPaymentGateways.enabled":
      orders.applyPaymentGateways,
    "exchanges.applyExchangeOrderPaymentGateways.upsellGateway":
      orders.upsellGateway,
    "exchanges.applyExchangeOrderPaymentGateways.returnValueGateway":
      orders.returnValueGateway,
    "exchanges.nonZeroValueExchangeStrategy":
      orders.nonZeroValueExchangeStrategy,
    // Cleared once orders stop being created at return creation, as legacy did.
    "exchanges.createLabelOnDraftOrderComplete":
      createsOnReturnCreation && orders.createLabelOnDraftOrderComplete,
    "exchanges.enableOnHoldOrders": createsOnReturnCreation,
    "exchanges.nonInstantExchangeOrderProvision":
      nonInstantProvisionFromTrigger(orders.createTrigger),
    "exchanges.onHoldOrdersOnUpsellOnly":
      orders.createTrigger === ExchangeCreateTrigger.CREATION_UPSELLS,
    "exchanges.addAdditionalExchangeTags": orders.addAdditionalExchangeTags,
    "exchanges.shouldRemoveHoldTagWhenRelease":
      orders.shouldRemoveHoldTagWhenRelease,
    "exchanges.separateQuantityForExchangeProducts":
      orders.separateQuantityForExchangeProducts,
    "exchanges.applyFeesAsLineItem": orders.applyFeesAsLineItem,
    "exchanges.applyFeesAsLineItemName":
      orders.applyFeesAsLineItemName.trim() || DEFAULT_FEE_LINE_ITEM_NAME,
    "exchanges.notes.disabled": orderNotes.disabled,
    "exchanges.notes.includeDate": orderNotes.includeDate,
    "exchanges.notes.includeExistingNote": orderNotes.includeExistingNote,
    "exchanges.notes.addNoteToOriginalOrders":
      orderNotes.addNoteToOriginalOrders,
    // Written whole: every sub-field is required once the object exists.
    "exchanges.fulfillmentRegions": {
      enabled: fulfillmentRegions.enabled,
      regionMappings: fulfillmentRegions.regionMappings.filter(
        (mapping) => mapping.country && mapping.locationIds.length > 0,
      ),
      defaultLocationIds: fulfillmentRegions.defaultLocationIds,
    },
  };
  const unset: string[] = [];

  if (isDefined(settings.exchanges.suffix)) {
    set["exchanges.suffix"] = null;
  }

  const shippingName = orders.exchangeShippingName.trim();
  if (shippingName) {
    set["exchanges.exchangeShippingName"] = shippingName;
  } else if (isDefined(settings.exchanges.exchangeShippingName)) {
    unset.push("exchanges.exchangeShippingName");
  }

  return { set, unset };
}
